import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { ActivitySectionDirective, RestaurantTableService } from '@core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HeaderComponent, PageHeaderBackSwipeConfig } from '@app/components/header/header.component';
import { CallWaiterButtonsComponent } from '@app/views/call-waiter-buttons/call-waiter-buttons.component';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { TextComponent } from '@app/components/text/text.component';
import { ImageTranslatePipe } from '@app/pipes/image-translate.pipe';
import { TextTranslatableSchema } from '@app/schemas/text';
import { PageViewServiceCentreSchema } from '@app/views/page-service-centre/page-service-centre.schema';
import { ImageComponent } from '@app/components/image/image.component';
import { LanguageService } from '@app/services';
import { ColorTranslatePipe } from '@app/pipes/color-translate.pipe';
import { HeaderBadgeTableNameComponent } from '@app/components/header/header-badge-table-name/header-badge-table-name.component';
import { HeaderBadgeTextComponent } from '@app/components/header/header-badge-text/header-badge-text.component';
import { HeaderTextComponent } from '@app/components/header/header-text/header-text.component';

@Component({
  selector: 'app-page-service-centre',
  templateUrl: 'page-service-centre.component.html',
  styleUrl: 'page-service-centre.component.scss',
  providers: [ColorTranslatePipe],
  imports: [
    ActivitySectionDirective,
    HeaderComponent,
    CallWaiterButtonsComponent,
    TextTranslatePipe,
    TextComponent,
    ImageTranslatePipe,
    ImageComponent,
    HeaderBadgeTableNameComponent,
    HeaderBadgeTextComponent,
    HeaderTextComponent,
  ],
  host: {
    class: 'page',
    '[style]': 'styles()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageServiceCentreComponent {

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly restaurantTable = inject(RestaurantTableService);
  private readonly languageService = inject(LanguageService);
  private readonly colorTranslatePipe = inject(ColorTranslatePipe);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  public readonly pageName = input.required<TextTranslatableSchema>();
  public readonly pageViewConfig = input.required<PageViewServiceCentreSchema>();

  protected readonly table = toSignal(this.restaurantTable.table$);

  protected readonly currentLanguage = toSignal(this.languageService.currentLanguage$, {
    requireSync: true,
  });

  protected readonly styles = computed(() => {
    const config = this.pageViewConfig();
    const language = this.currentLanguage();

    return {
      '--base-background-color': this.colorTranslatePipe.transform(config?.theme?.background?.color, language),
      '--base-foreground-color': this.colorTranslatePipe.transform(config?.theme?.foreground?.color, language),
    };
  });

}
